 
import { EntityListConfig, SortDirection, EntityListColumn, IEntityListConfig, SelectTypes } from '@mt-ng2/entity-list-module';
import { formatDate, formatCurrency } from '@angular/common';
import { IPickupPaymentDetail } from '../../model/interfaces/pickup-payment-detail';

export class PendingTransactionsEntityListConfig extends EntityListConfig {
    private getPickupCarrierName(detail: IPickupPaymentDetail): string {
        const orderCarrierName = detail.Pickup?.Orders?.find((order) => order.Carrier?.Name)?.Carrier?.Name;
        return orderCarrierName ?? detail.Pickup?.Carrier?.Name ?? 'N/A';
    }

    private formatOrderNumbersForDisplay(orderNumbers: string[], maxCount = 3): string {
        if (orderNumbers.length === 0) {
            return 'N/A';
        }

        if (orderNumbers.length <= maxCount) {
            return orderNumbers.join(', ');
        }

        const displayedNumbers = orderNumbers.slice(0, maxCount);
        return `${displayedNumbers.join(', ')}, ...`;
    }

    private isPickupPayment(detail: IPickupPaymentDetail): boolean {
        return detail?.PickupId != null && detail?.Pickup != null;
    }

    private isDeliveryPayment(detail: IPickupPaymentDetail): boolean {
        return detail?.DeliveryId != null && detail?.Delivery != null;
    }

    constructor() {
        const listConfig: IEntityListConfig = {
            columns: [
                new EntityListColumn({
                    accessorFunction: (detail: IPickupPaymentDetail): string => {
                        if (this.isPickupPayment(detail)) {
                            return detail.Pickup.PickupNumber ?? 'N/A';
                        }
                        if (this.isDeliveryPayment(detail)) {
                            return detail.Delivery.DeliveryNumber ?? 'N/A';
                        }
                        return 'N/A';
                    },
                    name: 'Pickup/Delivery Number',
                    sort: {
                        defaultDirection: SortDirection.Desc,
                        sortProperty: 'Pickup.PickupNumber',
                    },
                }),
                new EntityListColumn({
                    accessorFunction: (detail: IPickupPaymentDetail): string => {
                        if (this.isPickupPayment(detail)) {
                            const orders = detail.Pickup.Orders ?? [];
                            const orderNumbers = orders.map((order) => order.OrderNumber);
                            return this.formatOrderNumbersForDisplay(orderNumbers);
                        }
                        // Delivery payments don't have related orders
                        return 'N/A';
                    },
                    name: 'Related Order #',
                    sort: {
                        defaultDirection: SortDirection.Asc,
                        sortProperty: 'Pickup.Orders.FirstOrDefault().OrderNumber',
                    },
                }),
                new EntityListColumn({
                    accessorFunction: (detail: IPickupPaymentDetail): string => {
                        if (this.isPickupPayment(detail)) {
                            const orders = detail.Pickup.Orders ?? [];
                            const uniqueCustomers = Array.from(
                                new Set(orders.filter((order) => order.Customer?.Name).map((order) => order.Customer.Name)),
                            );
                            return uniqueCustomers.join(', ') || 'N/A';
                        }
                        if (this.isDeliveryPayment(detail)) {
                            return detail.Delivery.Customer?.Name ?? 'N/A';
                        }
                        return 'N/A';
                    },
                    name: 'Customer',
                    sort: {
                        defaultDirection: SortDirection.Asc,
                        sortProperty: 'Pickup.Orders.FirstOrDefault().Customer.Name',
                    },
                }),
                new EntityListColumn({
                    accessorFunction: function (detail: IPickupPaymentDetail): string {
                        return formatDate(detail.DateCreated, 'short', 'en-US');
                    },
                    name: 'Date Created',
                    sort: {
                        defaultDirection: SortDirection.Desc,
                        sortProperty: 'DateCreated',
                    },
                }),
                new EntityListColumn({
                    accessorFunction: function (detail: IPickupPaymentDetail): string {
                        return formatCurrency(detail.Amount, 'en-US', '$');
                    },
                    name: 'Pending Amount',
                    sort: {
                        defaultDirection: SortDirection.Desc,
                        sortProperty: 'Amount',
                    },
                }),
                new EntityListColumn({
                    accessorFunction: (detail: IPickupPaymentDetail): string => {
                        if (this.isPickupPayment(detail)) {
                            return this.getPickupCarrierName(detail);
                        }
                        if (this.isDeliveryPayment(detail)) {
                            return detail.Delivery.Carrier?.Name ?? 'N/A';
                        }
                        return 'N/A';
                    },
                    name: 'Carrier',
                    sort: {
                        defaultDirection: SortDirection.Asc,
                        sortProperty: 'Pickup.Carrier.Name',
                    },
                }),
            ],
        };
        listConfig.select = { type: SelectTypes.Single };
        super(listConfig);
    }
}
